import React from "react";

/**
 * Prime Vision logo system — usa ESCLUSIVAMENTE gli asset ufficiali del brand:
 *  - /brand/logo-v.png        →  V mark ufficiale (512x362, sfondo trasparente)
 *  - /brand/wordmark.png      →  wordmark completo "PRIME VISION" (1200x323)
 *  - /brand/wordmark-hd.png   →  wordmark alta risoluzione (6250x1684) per splash/login
 *
 * Nessun SVG ricostruito a mano: vengono mostrati i PNG ufficiali così come sono.
 */

const V_ASPECT = 362 / 512;       // 0.707
const WORDMARK_ASPECT = 323 / 1200; // 0.269

/**
 * V mark ufficiale Prime Vision — usalo per favicon, icone, sidebar, avatar.
 * `size` = altezza in px (mantiene proporzioni).
 */
export function PrimeVisionMark({ size = 40, className = "" }) {
  const h = size;
  const w = Math.round(h / V_ASPECT);
  return (
    <img
      src="/brand/logo-v.png"
      alt="Prime Vision"
      width={w}
      height={h}
      style={{ height: `${h}px`, width: "auto", objectFit: "contain", display: "block" }}
      className={className}
      data-testid="prime-vision-mark"
      draggable={false}
    />
  );
}

/**
 * Logo completo Prime Vision (V + wordmark) — usa il wordmark ufficiale PNG.
 *
 * Props:
 *  - size: altezza approssimativa del V mark di riferimento (px). Il wordmark
 *          viene scalato in proporzione (~3.7x del V).
 *  - showWordmark: se false, mostra solo il V mark.
 *  - theme: "dark" (default, su sfondo chiaro) | "light" (su sfondo scuro).
 *           NB: il PNG ufficiale è a colori brand (blu); su sfondi scuri usiamo
 *           lo stesso PNG (i blu sono studiati per restare leggibili).
 *  - hd: se true usa la versione alta risoluzione del wordmark (splash/login).
 */
export function PrimeVisionLogo({
  size = 40,
  showWordmark = true,
  theme = "dark",
  hd = false,
  className = "",
}) {
  // Se non serve il wordmark, ritorno solo il V mark
  if (!showWordmark) {
    return <PrimeVisionMark size={size} className={className} />;
  }

  // Wordmark intero (V + "PRIME VISION"): un unico PNG ufficiale.
  // Altezza target ≈ size * 1.4 per restare bilanciato visivamente vicino agli altri logotipi.
  const h = Math.round(size * 1.4);
  const src = hd ? "/brand/wordmark-hd.png" : "/brand/wordmark.png";
  return (
    <img
      src={src}
      alt="Prime Vision"
      style={{ height: `${h}px`, width: "auto", objectFit: "contain", display: "block" }}
      className={className}
      data-testid="prime-vision-logo"
      data-theme={theme}
      draggable={false}
    />
  );
}

export default PrimeVisionLogo;
